// ═══════════════════════════════════════════════════════════════════════════
// ProductManager
// Extraído VERBATIM de index.html v0.9.16.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { getIepsRateForGL } from "../dominio/impuestos.js";
import { etiquetaExistencias, resumenExistencias, faltanPorRegistrar,
         POR_QUE_FALTAN } from "../dominio/existencias.js";
import { fmt } from "../pdf/comunes.js";
import { puedeMembresia } from "../auth/permisos.js";
import { Card, Label, NumInput, SectionTitle } from "./basicos.jsx";
import { btnPrimary, btnSecondary, inputStyle, selectStyle } from "./estilos.js";
import { generateId } from "./utiles.js";

// El vendedor VE el catálogo —necesita saber qué hay y a cuánto para levantar
// un pedido— pero no lo toca. Antes esta pantalla no tenía ni una comprobación:
// cualquiera podía cambiar precios y borrar productos. El control real está en
// la RLS; esto es para que la interfaz no ofrezca lo que no corresponde.
export function ProductManager({ products, setProducts, membresia }) {
  const resumen = useMemo(() => resumenExistencias(products), [products]);
  const puedeEditar = puedeMembresia(membresia, "editar_productos");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const startNew = () => {
    setForm({ name: "", grossPrice: 0, netPrice: 0, category: "tinto", gl: 13.5, stock: 0,
              description: "", tastingNotes: "", imageBase64: "",
              pairing: "", labelStory: "",
              workTitle: "", workAuthor: "", workKind: "", workYear: "", workNote: "",
              artistName: "", artistNote: "", artworkNote: "" });
    setEditing("new");
  };

  const startEdit = (p) => { setForm({ ...p }); setEditing(p.id); };

  // Bidirectional price linking: grossPrice ↔ netPrice via IEPS + 16% IVA
  const calcNetFromGross = (gross, gl) => {
    const ieps = getIepsRateForGL(parseFloat(gl) || 0);
    return Math.round(gross * (1 + ieps / 100) * 1.16 * 100) / 100;
  };
  const calcGrossFromNet = (net, gl) => {
    const ieps = getIepsRateForGL(parseFloat(gl) || 0);
    return Math.round(net / ((1 + ieps / 100) * 1.16) * 100) / 100;
  };
  const setGrossPrice = (v) => setForm(f => ({ ...f, grossPrice: v, netPrice: calcNetFromGross(v, f.gl) }));
  const setNetPrice = (v) => setForm(f => ({ ...f, netPrice: v, grossPrice: calcGrossFromNet(v, f.gl) }));
  const setGl = (v) => setForm(f => ({ ...f, gl: v, netPrice: calcNetFromGross(f.grossPrice, v) }));

  const save = () => {
    if (!form.name.trim()) return;
    const gl = parseFloat(form.gl) || 0;
    const withIeps = { ...form, gl, iepsRate: getIepsRateForGL(gl) };
    if (editing === "new") {
      setProducts(prev => [...prev, { ...withIeps, id: generateId() }]);
    } else {
      setProducts(prev => prev.map(p => p.id === editing ? { ...withIeps } : p));
    }
    setEditing(null);
  };

  // El formulario ni se arma sin permiso: forzar el estado no abre nada.
  if (editing !== null && puedeEditar) return (
    <div>
      <SectionTitle>{editing === "new" ? "Nuevo Producto" : "Editar Producto"}</SectionTitle>
      <Card>
        {/* `auto-fit` en vez de dos columnas fijas: en un escritorio siguen
            siendo dos, y en un teléfono se vuelven una sola en vez de dejar
            cada campo en 160px. Importa más desde que este formulario lleva
            los diez campos de la ficha del cuadernillo. */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 16 }}>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Nombre del producto</Label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} style={inputStyle} />
          </div>
          <div>
            <Label>Precio bruto (sin impuestos)</Label>
            <NumInput value={form.grossPrice} onChange={setGrossPrice} style={inputStyle} />
            {form.grossPrice > 0 && (() => {
              const ieps = getIepsRateForGL(parseFloat(form.gl) || 0);
              const p8 = form.grossPrice * (1 + ieps/100) * 1.08;
              return <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
                @8% Frontera: <span style={{ color: "#70c9c9" }}>{fmt(p8)}</span>
              </div>;
            })()}
          </div>
          <div>
            <Label>Precio neto (con IEPS + 16% IVA)</Label>
            <NumInput value={form.netPrice} onChange={setNetPrice} style={inputStyle} />
          </div>
          <div>
            <Label>Categoría</Label>
            <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={selectStyle}>
              <option value="tinto">Tinto</option>
              <option value="blanco">Blanco</option>
              <option value="naranja">Naranja</option>
              <option value="vermu">Vermú</option>
              <option value="cerveza">Cerveza</option>
              <option value="destilado">Destilado</option>
              {/* «Cortesía» se retiró: no era una categoría de producto sino
                  una propiedad de las botellas vendidas. Se marca renglón por
                  renglón al levantar la remisión (migración 0060). */}
              <option value="otro">Otro</option>
            </select>
          </div>
          <div>
            <Label>Grados de Alcohol (° G.L.)</Label>
            <NumInput value={form.gl} onChange={setGl} style={inputStyle} step={0.5} />
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
              IEPS automático: <span style={{ color: "#c9a96e", fontWeight: 600 }}>{getIepsRateForGL(parseFloat(form.gl) || 0)}%</span>
              <span style={{ color: "#555" }}> ({(parseFloat(form.gl) || 0) <= 14 ? "≤14°" : (parseFloat(form.gl) || 0) <= 20 ? "14°–20°" : ">20°"})</span>
            </div>
          </div>
          <div>
            <Label>Inventario (botellas en existencia)</Label>
            <NumInput value={form.stock || 0} onChange={v => setForm(f => ({ ...f, stock: v }))} style={inputStyle} integer min={0} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Fotografía del producto</Label>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              {/* `contain`, no `cover`. Una botella es alta y angosta; en una
                  caja de 4:5 el `cover` la recorta por arriba y por abajo —
                  justo el cuello y la base, que es donde se reconoce el vino.
                  Sobra fondo a los lados y no importa: se ve entera. */}
              {form.imageBase64 && (
                <img src={form.imageBase64} alt="Producto" style={{ width: 80, height: 100, objectFit: "contain", borderRadius: 8, border: "1px solid #333", background: "#0a0a0a" }} />
              )}
              <div style={{ flex: 1 }}>
                <input type="file" accept="image/*" onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  if (file.size > 2 * 1024 * 1024) { alert("La imagen debe ser menor a 2 MB"); return; }
                  const reader = new FileReader();
                  reader.onload = () => setForm(f => ({ ...f, imageBase64: reader.result }));
                  reader.readAsDataURL(file);
                }} style={{ fontSize: 12, color: "#888" }} />
                {form.imageBase64 && (
                  <button onClick={() => setForm(f => ({ ...f, imageBase64: "" }))} style={{ ...btnSecondary, padding: "2px 8px", fontSize: 10, marginTop: 4, borderColor: "#3a2020", color: "#c97070" }}>
                    Quitar imagen
                  </button>
                )}
                <div style={{ fontSize: 10, color: "#555", marginTop: 4 }}>JPG, PNG o WebP. Máx 2 MB. Se almacena con los datos.</div>
              </div>
            </div>
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Descripción del producto</Label>
            <textarea value={form.description || ""} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="Breve descripción del vino, varietales, origen, añada..."
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Notas de cata</Label>
            <textarea value={form.tastingNotes || ""} onChange={e => setForm(f => ({ ...f, tastingNotes: e.target.value }))}
              placeholder="Vista, nariz, boca..."
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Maridaje</Label>
            <input value={form.pairing || ""} onChange={e => setForm(f => ({ ...f, pairing: e.target.value }))}
              placeholder="Carnes rojas y comida condimentada, como la birria."
              style={inputStyle} />
          </div>

          {/* ── Lo que se imprime en el cuadernillo ─────────────────────────
              Todo esto es opcional y todo lo que se deje vacío desaparece del
              PDF: ni su título, ni su hueco. Una vinícola que solo quiera
              foto, cata y maridaje tiene una página legítima sin tocar nada
              de aquí abajo. */}
          <div style={{ gridColumn: "1/-1", marginTop: 6, paddingTop: 14, borderTop: "1px solid #2a2a2a" }}>
            <div style={{ fontSize: 12, color: "#c9a96e", fontWeight: 600, marginBottom: 2 }}>
              La ficha del cuadernillo
            </div>
            <div style={{ fontSize: 11, color: "#666", lineHeight: 1.6 }}>
              Lo que un vendedor le enseña a un restaurantero. Lo que quede vacío
              no se imprime — se puede llenar después sin que la página se vea rota.
            </div>
          </div>

          <div style={{ gridColumn: "1/-1" }}>
            <Label>La historia detrás de la etiqueta</Label>
            <textarea value={form.labelStory || ""} onChange={e => setForm(f => ({ ...f, labelStory: e.target.value }))}
              placeholder="Qué une a este vino con su etiqueta. Es el texto que el vendedor cuenta en voz alta."
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} />
          </div>

          <div>
            <Label>Obra · título</Label>
            <input value={form.workTitle || ""} onChange={e => setForm(f => ({ ...f, workTitle: e.target.value }))}
              placeholder="Rayuela" style={inputStyle} />
          </div>
          <div>
            <Label>Obra · autor</Label>
            <input value={form.workAuthor || ""} onChange={e => setForm(f => ({ ...f, workAuthor: e.target.value }))}
              placeholder="Julio Cortázar" style={inputStyle} />
          </div>
          <div>
            <Label>Obra · tipo</Label>
            <input value={form.workKind || ""} onChange={e => setForm(f => ({ ...f, workKind: e.target.value }))}
              placeholder="novela, cuento, obra de teatro" style={inputStyle} />
          </div>
          <div>
            <Label>Obra · año</Label>
            <input value={form.workYear || ""} onChange={e => setForm(f => ({ ...f, workYear: e.target.value }))}
              placeholder="1963" style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Obra · de qué trata y por qué acompaña a este vino</Label>
            <textarea value={form.workNote || ""} onChange={e => setForm(f => ({ ...f, workNote: e.target.value }))}
              style={{ ...inputStyle, minHeight: 60, resize: "vertical" }} />
          </div>

          <div>
            <Label>Arte · quién ilustró la etiqueta</Label>
            <input value={form.artistName || ""} onChange={e => setForm(f => ({ ...f, artistName: e.target.value }))}
              placeholder="Zaira Zamudio" style={inputStyle} />
          </div>
          <div>
            <Label>Arte · qué se ve en la ilustración</Label>
            <input value={form.artworkNote || ""} onChange={e => setForm(f => ({ ...f, artworkNote: e.target.value }))}
              placeholder="Dos figuras a los lados de una rayuela." style={inputStyle} />
          </div>
          <div style={{ gridColumn: "1/-1" }}>
            <Label>Arte · semblanza de quien ilustra</Label>
            <textarea value={form.artistNote || ""} onChange={e => setForm(f => ({ ...f, artistNote: e.target.value }))}
              style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} />
            <div style={{ fontSize: 10, color: "#8a7a4a", marginTop: 4, lineHeight: 1.6 }}>
              Este campo llega vacío a propósito y nadie lo llenó por ti. Es sobre una
              persona real: una semblanza inventada se imprime igual de bien que una
              cierta, y quien la lea no puede distinguirlas.
            </div>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 24, justifyContent: "flex-end" }}>
          <button onClick={() => setEditing(null)} style={btnSecondary}>Cancelar</button>
          <button onClick={save} style={btnPrimary}>💾 Guardar</button>
        </div>
      </Card>
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 }}>
        {/* El total sumaba TODO, así que un −2 se restaba de las demás botellas
            y desaparecía dentro del gran total: dos defectos tapándose entre
            ellos. Lo que hay y lo que falta por registrar son hechos distintos
            y se cuentan aparte. */}
        <SectionTitle sub={`${products.length} productos · ${resumen.botellas} botellas en inventario`}>
          Productos
        </SectionTitle>
        {puedeEditar && (
          <button onClick={startNew} style={btnPrimary}>+ Nuevo Producto</button>
        )}
      </div>

      {/* ── El aviso de sobreventa. Avisa, no bloquea ─────────────────────────
          El operador: "de la sobreventa, solo un warning. Si se vendió hay
          botellas que no se registraron."
          Bloquear la venta sería el arreglo equivocado: pararía lo único que sí
          ocurrió para proteger un número que ya estaba mal. */}
      {resumen.faltan > 0 && (
        <Card style={{ marginBottom: 14, padding: "12px 14px",
                       background: "#1a1408", border: "1px solid #3a331a" }}>
          <div style={{ fontSize: 13, color: "#facc15", marginBottom: 6 }}>
            ⚠️ Faltan {resumen.faltan} botella{resumen.faltan === 1 ? "" : "s"} por
            registrar, en {resumen.productosConFaltante} producto
            {resumen.productosConFaltante === 1 ? "" : "s"}
          </div>
          <div style={{ fontSize: 11, color: "#8a8266", lineHeight: 1.7, marginBottom: 8 }}>
            {POR_QUE_FALTAN}
          </div>
          <div style={{ fontSize: 11, color: "#a09878" }}>
            {faltanPorRegistrar(products).map(f => (
              <div key={f.id}>· {f.nombre} — faltan {f.faltan}</div>
            ))}
          </div>
        </Card>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
        {products.map(p => (
          <Card key={p.id} style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 12 }}>
              {p.imageBase64 && (
                <img src={p.imageBase64} alt="" style={{ width: 50, height: 65, objectFit: "contain", borderRadius: 6, border: "1px solid #333", background: "#0a0a0a", flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: p.category === "tinto" ? "#2a1520" : p.category === "blanco" ? "#202a20" : p.category === "cerveza" ? "#2a2520" : p.category === "cortesia" ? "#1a2a2a" : "#2a2015", color: p.category === "tinto" ? "#c97070" : p.category === "blanco" ? "#70c970" : p.category === "cerveza" ? "#c9a050" : p.category === "cortesia" ? "#70c9c9" : "#c9a96e" }}>
                      {p.category}
                    </span>
                    {/* Un −2 decía "Sin stock", igual que un 0. El hecho de que
                        falten botellas por registrar no estaba en ninguna
                        pantalla; ahora se nombra. La frase y el tono viven en el
                        dominio, no repartidos por aquí. */}
                    {(() => {
                      const e = etiquetaExistencias(p);
                      const paleta = {
                        bien:   { fondo: "#0d1f0d", color: "#4ade80" },
                        neutro: { fondo: "#1a1a1a", color: "#888"    },
                        aviso:  { fondo: "#1f1a0d", color: "#facc15" },
                      }[e.tono];
                      return (
                        <span title={e.tono === "aviso" ? POR_QUE_FALTAN : undefined}
                          style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4,
                                   background: paleta.fondo, color: paleta.color }}>
                          {e.tono === "aviso" ? `⚠️ ${e.texto}` : e.texto}
                        </span>
                      );
                    })()}
                  </div>
                  <div style={{ display: "flex", gap: 4 }}>
                    {puedeEditar && (
                    <>
                    <button onClick={() => startEdit(p)} style={{ ...btnSecondary, padding: "2px 10px", fontSize: 11 }}>✏️</button>
                    <button onClick={() => {
                      if (confirm(`¿Eliminar "${p.name}"?\n\nEsta acción no se puede deshacer.`)) {
                        setProducts(prev => prev.filter(pr => pr.id !== p.id));
                      }
                    }} style={{ ...btnSecondary, padding: "2px 10px", fontSize: 11, borderColor: "#4a2020", color: "#c97070" }}>🗑</button>
                    </>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 6, lineHeight: 1.3 }}>{p.name}</div>
                {p.description && <div style={{ fontSize: 11, color: "#888", marginBottom: 6, lineHeight: 1.3 }}>{p.description.substring(0, 80)}{p.description.length > 80 ? "..." : ""}</div>}
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <div><span style={{ color: "#666", fontSize: 10 }}>BRUTO</span><br /><span style={{ color: "#888" }}>{fmt(p.grossPrice)}</span></div>
                  <div style={{ textAlign: "center" }}><span style={{ color: "#666", fontSize: 10 }}>NETO @8%</span><br /><span style={{ color: "#70c9c9" }}>{fmt(p.grossPrice * (1 + (p.iepsRate || 26.5)/100) * 1.08)}</span></div>
                  <div style={{ textAlign: "right" }}><span style={{ color: "#666", fontSize: 10 }}>NETO @16%</span><br /><span style={{ color: "#c9a96e" }}>{fmt(p.netPrice)}</span></div>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
